"use client";

import { CircleCheck, Hourglass } from "lucide-react";
import { Button, LinkButton, buttonClass } from "@/components/ui/Button";
import { openRegistration, useRegistrationDraft } from "@/components/registration/registration";
import { inscricaoPath } from "@/lib/address";
import { track } from "@/lib/analytics";
import { euros } from "@/lib/format";
import { isPreview } from "@/lib/preview";
import type { EventSummary } from "@/lib/types/eventList";
import { isSoldOutFor, opensAsMyEvent, showsRegisterButton } from "@/components/explore/eventCardModel";
import { WaitingListButton, WaitingNote, useWaitingPlaces } from "@/components/waiting/waitingList";

export { opensAsMyEvent, showsRegisterButton, isSoldOutFor };

/**
 * What a client already registered is told in place of the prices or the way
 * to register: on their card in Explore, and on the event's own page over the
 * way to their inscrição (the current app's registeredPanel).
 */
export function RegisteredPanel({ event, className = "" }: { event: EventSummary; className?: string }) {
  const awaiting = !!event.awaiting_payment;
  const due = Number(event.amount_due) || 0;
  return (
    <div
      className={`flex items-center gap-2.5 rounded-[var(--radius-sm)] px-3 py-2.5 text-[13px] leading-snug ${
        awaiting ? "bg-amber-soft text-[#8a4b12]" : "bg-brand-mint text-[#256b43]"
      } ${className}`}
    >
      {awaiting ? <Hourglass size={22} className="shrink-0" /> : <CircleCheck size={22} className="shrink-0" />}
      <div>
        {awaiting ? (
          <>
            <strong className="block font-bold">A aguardar pagamento</strong>
            <span className="block">{due > 0 ? `A tua inscrição está feita. Falta pagar ${euros(due)}.` : "A tua inscrição está feita."}</span>
          </>
        ) : (
          <>
            <strong className="block font-bold">A tua inscrição está feita</strong>
            <span className="block">{due > 0 ? `Falta pagar ${euros(due)}.` : "Está tudo tratado. Até lá!"}</span>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * The way to A minha inscrição for a client going to an event: the one button,
 * with the one wording, on the event's own page and on its card in a list.
 * `from` is which of the two GA is told it was pressed on.
 */
export function InscricaoButton({
  event,
  from,
  size = "lg",
  full = true,
  className = "",
}: {
  event: EventSummary;
  from: "event_page" | "list";
  size?: "sm" | "md" | "lg";
  full?: boolean;
  className?: string;
}) {
  return (
    <LinkButton
      href={inscricaoPath(event)}
      size={size}
      full={full}
      className={className}
      onClick={() => track(`open_inscricao_from_${from}`, { item_id: String(event.id) })}
    >
      Ver a minha inscrição
    </LinkButton>
  );
}

/**
 * The way to register for an event, wherever it is drawn (the event's page, its
 * pinned bar), from the event's own fields (the current app's registerButton
 * in models/registrationDrafts.js):
 *
 *  - a client going to it: the "A tua inscrição está feita" panel over the way
 *    to A minha inscrição;
 *  - registered and still to pay: a disabled button saying so, which opens nothing;
 *  - no places left for anyone else: the way onto the event's waiting list, or
 *    what says they are on it (WaitingListButton);
 *  - otherwise Inscrever-me, or Continuar inscrição while a registration for
 *    this event was started and put aside; with a line under it for a client
 *    waiting for more places than there are.
 *
 * Nothing while registration is not open and the client is not going.
 */
export function RegisterButton({
  event,
  size = "lg",
  full = true,
  panel = true,
  className = "",
}: {
  event: EventSummary;
  size?: "sm" | "md" | "lg";
  full?: boolean;
  /** draw the panel over the inscrição link; false for a bar with no room */
  panel?: boolean;
  className?: string;
}) {
  const draft = useRegistrationDraft(event.id);
  const waiting = useWaitingPlaces(event);

  if (!showsRegisterButton(event)) {
    return null;
  }

  if (opensAsMyEvent(event)) {
    return (
      <div className={className}>
        {panel && <RegisteredPanel event={event} className="mb-3" />}
        <InscricaoButton event={event} from="event_page" size={size} full={full} />
      </div>
    );
  }

  if (event.is_registered && event.awaiting_payment) {
    // a plain link: it leaves for Eupago's page. Not from a preview, where it would make a real one
    if (event.payment_link && !isPreview()) {
      const due = Number(event.amount_due) || 0;
      return (
        <a
          href={event.payment_link}
          onClick={() => track("pay_open", { item_id: String(event.id), from: "event_page" })}
          className={buttonClass("primary", size, full, className)}
        >
          {due > 0 ? `Pagar ${euros(due)}` : "Pagar"}
        </a>
      );
    }
    return (
      <Button variant="amber" size={size} full={full} disabled className={className} icon={<Hourglass size={18} />}>
        A aguardar pagamento
      </Button>
    );
  }

  if (isSoldOutFor(event)) {
    return <WaitingListButton event={event} size={size} full={full} panel={panel} className={className} />;
  }

  const register = (
    <Button size={size} full={full} className={waiting ? "" : className} onClick={() => openRegistration(event.id)}>
      {draft ? "Continuar inscrição" : "Inscrever-me"}
    </Button>
  );
  if (waiting > 0 && full && panel) {
    return (
      <div className={className}>
        {register}
        <WaitingNote event={event} places={waiting} className="mt-1.5" />
      </div>
    );
  }
  return register;
}
